// Firebase Authentication Service for Phone/SMS OTP
import { 
  signInWithPhoneNumber, 
  PhoneAuthProvider, 
  signInWithCredential,
  RecaptchaVerifier,
  ConfirmationResult,
  User
} from 'firebase/auth';
import { auth } from '../config/firebase';

export interface PhoneAuthResult {
  success: boolean;
  message: string;
  verificationId?: string;
  user?: User;
}

class FirebaseAuthService {
  private recaptchaVerifier: RecaptchaVerifier | null = null;
  private confirmationResult: ConfirmationResult | null = null;

  /**
   * Initialize reCAPTCHA verifier
   * @param containerId - HTML element ID where reCAPTCHA will be rendered
   * @param useVisible - Use visible reCAPTCHA instead of invisible (for debugging)
   */
  initializeRecaptcha(containerId: string = 'recaptcha-container', useVisible: boolean = false): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        // Ensure we're in browser environment
        if (typeof window === 'undefined') {
          reject(new Error('reCAPTCHA can only be initialized in browser environment'));
          return;
        }

        // Clear existing verifier if any
        if (this.recaptchaVerifier) {
          try {
            this.recaptchaVerifier.clear();
          } catch (e) {
            // Ignore errors when clearing
          }
          this.recaptchaVerifier = null;
        }

        // Ensure container exists
        let container = document.getElementById(containerId);
        if (!container) {
          // Create container if it doesn't exist
          container = document.createElement('div');
          container.id = containerId;
          container.style.display = 'none';
          container.style.position = 'absolute';
          container.style.left = '-9999px';
          document.body.appendChild(container);
        }

        // Wait a bit to ensure DOM is ready
        setTimeout(() => {
          try {
            // Create new reCAPTCHA verifier with proper configuration
            const recaptchaConfig: any = {
              size: useVisible ? 'normal' : 'invisible',
              callback: (response: any) => {
                console.log('reCAPTCHA verified successfully, response:', response);
                resolve();
              },
              'expired-callback': () => {
                console.warn('reCAPTCHA expired');
                this.recaptchaVerifier = null;
                reject(new Error('reCAPTCHA expired. Please try again.'));
              },
              'error-callback': (error: any) => {
                console.error('reCAPTCHA error:', error);
                this.recaptchaVerifier = null;
                reject(new Error(`reCAPTCHA error: ${error?.message || 'Unknown error'}`));
              }
            };

            // For visible reCAPTCHA, add theme
            if (useVisible) {
              recaptchaConfig.theme = 'light';
            }

            this.recaptchaVerifier = new RecaptchaVerifier(auth, containerId, recaptchaConfig);

            // Render reCAPTCHA
            this.recaptchaVerifier.render().then((widgetId: number) => {
              console.log('reCAPTCHA rendered successfully, widget ID:', widgetId);
              resolve();
            }).catch((error: any) => {
              console.error('reCAPTCHA render error:', error);
              this.recaptchaVerifier = null;
              reject(new Error(`Failed to render reCAPTCHA: ${error?.message || 'Unknown error'}`));
            });
          } catch (error: any) {
            console.error('reCAPTCHA initialization error:', error);
            this.recaptchaVerifier = null;
            reject(new Error(`Failed to initialize reCAPTCHA: ${error?.message || 'Unknown error'}`));
          }
        }, 100);
      } catch (error: any) {
        console.error('reCAPTCHA setup error:', error);
        this.recaptchaVerifier = null;
        reject(new Error(`Failed to setup reCAPTCHA: ${error?.message || 'Unknown error'}`));
      }
    });
  }

  /**
   * Send OTP to phone number
   * @param phoneNumber - Phone number in E.164 format (e.g., +1234567890)
   */
  async sendOTP(phoneNumber: string): Promise<PhoneAuthResult> {
    try {
      // Ensure phone number is in E.164 format
      const formattedPhone = this.formatPhoneNumber(phoneNumber);
      
      if (!formattedPhone) {
        return {
          success: false,
          message: 'Invalid phone number format. Please use international format (e.g., +1234567890)'
        };
      }

      // Initialize reCAPTCHA if not already done or if it's been cleared
      if (!this.recaptchaVerifier) {
        await this.initializeRecaptcha();
      }

      // Verify reCAPTCHA is ready
      if (!this.recaptchaVerifier) {
        throw new Error('reCAPTCHA verifier is not initialized');
      }

      // Wait a moment to ensure reCAPTCHA is fully ready
      // For invisible reCAPTCHA, Firebase will automatically verify when sending OTP
      // But we need to ensure the widget is fully rendered
      await new Promise(resolve => setTimeout(resolve, 200));
      
      console.log('Attempting to send OTP with reCAPTCHA verifier');

      // Send OTP
      const confirmationResult = await signInWithPhoneNumber(
        auth,
        formattedPhone,
        this.recaptchaVerifier
      );

      this.confirmationResult = confirmationResult;

      return {
        success: true,
        message: 'OTP sent successfully to your phone number',
        verificationId: confirmationResult.verificationId
      };
    } catch (error: any) {
      console.error('Error sending OTP:', error);
      
      let errorMessage = 'Failed to send OTP. Please try again.';
      
      if (error.code === 'auth/invalid-phone-number') {
        errorMessage = 'Invalid phone number format. Please use international format (e.g., +1234567890)';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many requests. Please try again later.';
      } else if (error.code === 'auth/quota-exceeded') {
        errorMessage = 'SMS quota exceeded. Please try again later.';
      } else if (error.code === 'auth/invalid-app-credential' || error.message?.includes('INVALID_APP_CREDENTIAL')) {
        errorMessage = 'Firebase configuration error. Please ensure:\n1. Phone authentication is enabled in Firebase Console\n2. Your domain (localhost) is authorized\n3. API key restrictions allow Identity Toolkit API\n4. reCAPTCHA is properly configured';
        console.error('INVALID_APP_CREDENTIAL error details:', {
          code: error.code,
          message: error.message,
          fullError: error
        });
        // Clear verifier to force re-initialization
        this.recaptchaVerifier = null;
      } else if (error.message) {
        errorMessage = error.message;
      }

      return {
        success: false,
        message: errorMessage
      };
    }
  }

  /**
   * Verify OTP code
   * @param code - 6-digit OTP code
   */
  async verifyOTP(code: string): Promise<PhoneAuthResult> {
    try {
      if (!this.confirmationResult) {
        return {
          success: false,
          message: 'No verification in progress. Please request OTP first.'
        };
      }

      // Verify the code
      const userCredential = await this.confirmationResult.confirm(code);
      const user = userCredential.user;

      // Clear confirmation result after successful verification
      this.confirmationResult = null;

      return {
        success: true,
        message: 'Phone number verified successfully',
        user: user
      };
    } catch (error: any) {
      console.error('Error verifying OTP:', error);
      
      let errorMessage = 'Invalid OTP code. Please try again.';
      
      if (error.code === 'auth/invalid-verification-code') {
        errorMessage = 'Invalid OTP code. Please check and try again.';
      } else if (error.code === 'auth/code-expired') {
        errorMessage = 'OTP code has expired. Please request a new one.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      return {
        success: false,
        message: errorMessage
      };
    }
  }

  /**
   * Verify OTP using verification ID and code (alternative method)
   * @param verificationId - Verification ID from sendOTP
   * @param code - 6-digit OTP code
   */
  async verifyOTPWithId(verificationId: string, code: string): Promise<PhoneAuthResult> {
    try {
      const credential = PhoneAuthProvider.credential(verificationId, code);
      const userCredential = await signInWithCredential(auth, credential);
      const user = userCredential.user;

      return {
        success: true,
        message: 'Phone number verified successfully',
        user: user
      };
    } catch (error: any) {
      console.error('Error verifying OTP:', error);
      
      let errorMessage = 'Invalid OTP code. Please try again.';
      
      if (error.code === 'auth/invalid-verification-code') {
        errorMessage = 'Invalid OTP code. Please check and try again.';
      } else if (error.code === 'auth/code-expired') {
        errorMessage = 'OTP code has expired. Please request a new one.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      return {
        success: false,
        message: errorMessage
      };
    }
  }

  /**
   * Get current Firebase user
   */
  getCurrentUser(): User | null {
    return auth.currentUser;
  }

  /**
   * Get Firebase ID token
   */
  async getIdToken(): Promise<string | null> {
    const user = auth.currentUser;
    if (user) {
      return await user.getIdToken();
    }
    return null;
  }

  /**
   * Sign out current user
   */
  async signOut(): Promise<void> {
    await auth.signOut();
    this.confirmationResult = null;
  }

  /**
   * Format phone number to E.164 format
   * @param phoneNumber - Phone number in any format
   */
  private formatPhoneNumber(phoneNumber: string): string | null {
    // Remove all non-digit characters except +
    let cleaned = phoneNumber.replace(/[^\d+]/g, '');

    // If it doesn't start with +, add it (assuming default country code)
    if (!cleaned.startsWith('+')) {
      // You can customize this based on your default country
      // For now, we'll require the + prefix
      return null;
    }

    // Basic validation: should be between 10-15 digits after +
    const digitsOnly = cleaned.replace('+', '');
    if (digitsOnly.length < 10 || digitsOnly.length > 15) {
      return null;
    }

    return cleaned;
  }

  /**
   * Clean up reCAPTCHA verifier
   */
  cleanup(): void {
    if (this.recaptchaVerifier) {
      this.recaptchaVerifier.clear();
      this.recaptchaVerifier = null;
    }
    this.confirmationResult = null;
  }
}

// Export singleton instance
export const firebaseAuthService = new FirebaseAuthService();
export default firebaseAuthService;

